/**
 * AdminFounderSummary — 30-second PMF read for /admin/analytics.
 *
 * 8 headline metrics in a single glanceable card, sourced from the
 * first-party analytics pipeline (privacy-safe; includes Stealth Mode
 * users with their anonymized flag). Designed for the founder to walk
 * past a screen and read the numbers in under 30 seconds.
 *
 * Layout: 8-tile grid — 4 across on desktop, 2 across on mobile.
 * Each tile is intentionally sparse: big number, soft eyebrow label,
 * optional contextual mini-stat below.
 *
 * Source: GET /api/admin/analytics/founder-summary?days=7
 *
 * The default window is 7 days — long enough to smooth daily noise,
 * short enough to be "current". Pass `days={n}` to override.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import PostHogReporting from "./PostHogReporting";
import { Eye, Sparkles, Camera, CheckCircle2, MousePointer, ChevronsRight, CreditCard, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString() : "—";

const fmtPct = (v) =>
  v === null || v === undefined ? "—" : `${v}%`;

const Tile = ({ icon, label, value, hint, testid, accent }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:bg-white/[0.07] transition-colors"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-3">
      {icon}
      <span>{label}</span>
    </div>
    <div
      className="text-3xl sm:text-4xl text-white tabular-nums font-light"
      style={accent ? { color: accent } : undefined}
    >
      {value}
    </div>
    {hint && (
      <div className="text-[11px] text-white/45 mt-2 leading-snug">{hint}</div>
    )}
  </div>
);

export default function AdminFounderSummary({ days = 7 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/admin/analytics/founder-summary?days=${days}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status || "error");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (loading) {
    return (
      <div
        className="bg-gradient-to-br from-[#1B1316] to-[#2A1E22] border border-[#D4B483]/20 rounded-3xl p-7 mb-8"
        data-testid="founder-summary-loading"
      >
        <div className="text-white/40 text-sm">Reading founder summary…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-gradient-to-br from-[#1B1316] to-[#2A1E22] border border-[#F4B8C0]/30 rounded-3xl p-7 mb-8"
        data-testid="founder-summary-error"
      >
        <div className="text-[#F4B8C0] text-sm">
          Couldn&rsquo;t load founder summary ({error}).
        </div>
      </div>
    );
  }

  if (!data) return null;
  if (data.provider === "posthog") return <PostHogReporting status={data} />;

  return (
    <div
      className="bg-gradient-to-br from-[#1B1316] to-[#2A1E22] border border-[#D4B483]/25 rounded-3xl p-6 sm:p-8 mb-8"
      data-testid="founder-summary-card"
    >
      <div className="flex items-baseline justify-between mb-1">
        <h2
          className="text-xl sm:text-2xl text-white font-light"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Founder dashboard
        </h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483]">
          Last {data.window_days || days} days
        </span>
      </div>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-white/50 max-w-2xl">
          Product-market fit in under 30 seconds. All numbers come from the
          privacy-safe first-party pipeline — stealth users counted with
          their anonymized flag.
        </p>
      </div>

      {/* Funnel scope banner — founders kept comparing demo_completed
          to routine_generated and reading "0 vs 6" as a bug. They're
          two different funnels. Make it loud, once, here. */}
      <div
        className="mb-6 px-4 py-3 rounded-2xl bg-[#9B6A6C]/15 border border-[#D4B483]/30 text-[12px] text-white/85 leading-relaxed"
        data-testid="founder-funnel-scope-banner"
      >
        <strong className="text-[#D4B483]">Scope note:</strong> the tiles below
        count the <em>pre-signup</em> <code className="bg-white/10 px-1 py-0.5 rounded text-[10px]">/demo</code> funnel only.
        Signed-in routine generation has its own metric — see{" "}
        <span className="text-[#D4B483]">Routines generated (24h)</span> in the daily digest +{" "}
        <span className="text-[#D4B483]">/admin/users</span> for the per-user breakdown.
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Tile
          icon={<Eye className="h-3.5 w-3.5" />}
          label="Visitors"
          value={fmt(data.visitors)}
          hint="Distinct sessions w/ page_view"
          testid="founder-tile-visitors"
        />
        <Tile
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="/demo started"
          value={fmt(data.demo_started)}
          hint="Cold guests who hit /demo (pre-signup)"
          testid="founder-tile-demo-started"
        />
        <Tile
          icon={<CheckCircle2 className="h-3.5 w-3.5" />}
          label="/demo completed"
          value={fmt(data.demo_completed)}
          hint="Pre-signup demo → received a sample routine"
          testid="founder-tile-demo-completed"
        />
        <Tile
          icon={<Camera className="h-3.5 w-3.5" />}
          label="/demo completion rate"
          value={fmtPct(data.demo_completion_rate)}
          hint="Pre-signup completed / started"
          testid="founder-tile-demo-completion-rate"
          accent={
            data.demo_completion_rate !== null &&
            data.demo_completion_rate >= 50
              ? "#9DDDB0"
              : undefined
          }
        />
        <Tile
          icon={<MousePointer className="h-3.5 w-3.5" />}
          label="Signup clicks"
          value={fmt(data.signup_clicks)}
          hint="Clicked /demo → signup CTA"
          testid="founder-tile-signup-clicks"
        />
        <Tile
          icon={<ChevronsRight className="h-3.5 w-3.5" />}
          label="Demo → signup rate"
          value={fmtPct(data.demo_to_signup_rate)}
          hint="clicks / completed"
          testid="founder-tile-demo-to-signup-rate"
          accent={
            data.demo_to_signup_rate !== null &&
            data.demo_to_signup_rate >= 25
              ? "#9DDDB0"
              : undefined
          }
        />
        <Tile
          icon={<CreditCard className="h-3.5 w-3.5" />}
          label="Subs converted"
          value={fmt(data.subscription_completed)}
          hint="Pro activations"
          testid="founder-tile-subs"
          accent="#D4B483"
        />
        <Tile
          icon={<Lock className="h-3.5 w-3.5" />}
          label="Stealth users"
          value={fmtPct(data.stealth_pct)}
          hint="% of all sessions"
          testid="founder-tile-stealth-pct"
        />
      </div>
    </div>
  );
}
